// Updated 2025-11-05: Add google recaptcha support
// Updated 2026-04-02: Build fix

// Add this declaration at the top of your file to inform TypeScript
// about the globally available grecaptcha object.
declare const grecaptcha: any;

export interface IFormHelper {
    // you can pass in a selector or the actual elements
    form: string | HTMLFormElement;
    submitButton?: string | HTMLInputElement | HTMLButtonElement;
}

// valid elements
type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

interface IValidationElement {
    field: FieldElement;
    messageField?: Element;
    requiredMessage?: string;
    phoneMessage?: string;
    emailMessage?: string;
    creditcardMessage?: string; // this currently doesn't do anything!
    regexMessage?: string;
    maxlengthMessage?: string;
    minlengthMessage?: string;
}

type doFixup = (validElement: IValidationElement, attrval: string) => void;

const fixups: { [attrkey: string]: doFixup } = {
    "data-val-required": (validElement, attrval) => {
        validElement.requiredMessage = attrval;
        validElement.field.setAttribute("required", "");
    },
    "data-val-email": (validElement, attrval) => {
        validElement.emailMessage = attrval;
    },
    "data-val-phone": (validElement, attrval) => {
        validElement.phoneMessage = attrval;
    },
    "data-val-creditcard": (validElement, attrval) => {
        validElement.creditcardMessage = attrval;
    },
    "data-val-maxlength": (validElement, attrval) => {
        validElement.maxlengthMessage = attrval;
    },
    "data-val-maxlength-max": (validElement, attrval) => {
        validElement.field.setAttribute("maxlength", attrval);
    },
    "data-val-minlength": (validElement, attrval) => {
        validElement.minlengthMessage = attrval;
    },
    "data-val-minlength-min": (validElement, attrval) => {
        validElement.field.setAttribute("minlength", attrval);
    },
    "data-val-regex": (validElement, attrval) => {
        validElement.regexMessage = attrval;
    },
    "data-val-regex-pattern": (validElement, attrval) => {
        validElement.field.setAttribute("pattern", attrval);
    },
};

const DEFAULTINVALIDMESSAGE = "Field is not valid";

// Includes various functions to make forms more user friendly
// 100% javascript... no jquery
// related topics:
// https://www.falldowngoboone.com/blog/native-form-validation-with-javascript/
// https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation
export class FormHelper {
    submitMessage: string;
    form!: HTMLFormElement;
    submitButtons!: (HTMLInputElement | HTMLButtonElement)[];
    isSubmitted: boolean;
    checkDoubleSubmit: boolean;
    checkValid: boolean;
    validationElements: Map<Element, IValidationElement>;
    submitConfirmWarning: string | null;

    // Recaptcha properties
    recaptchaSiteKey?: string;
    useRecaptcha : boolean;
    recaptchaField? : HTMLInputElement | null;

    constructor(setup: IFormHelper) {
        const self = this;
        if (typeof setup.form === "string") {
            const form = document.querySelector(setup.form);
            if (!form || !(form instanceof HTMLFormElement)) {
                throw new Error(
                    "FormHelper: form element not found or is not a valid form.",
                );
            } else {
                this.form = form;
            }
        } else {
            this.form = setup.form;
        }
        if (this.form) {
            if (setup.submitButton) {
                if (typeof setup.submitButton === "string") {
                    this.submitButtons = FormHelper.getValidButtons(
                        document.querySelectorAll(setup.submitButton),
                    );
                } else {
                    this.submitButtons = [setup.submitButton];
                }
            } else {
                const buttons = this.form.querySelectorAll(
                    'input[type="submit"], button[type="submit"]',
                );
                this.submitButtons = FormHelper.getValidButtons(buttons);
            }
        } else {
            if (setup.submitButton) {
                if (typeof setup.submitButton === "string") {
                    this.submitButtons = FormHelper.getValidButtons(
                        document.querySelectorAll(setup.submitButton),
                    );
                } else {
                    this.submitButtons = [setup.submitButton];
                }
            }
            else {
                console.warn("FormHelper: form element not found, so submit buttons cannot be found.");
                this.submitButtons = [];
            }
        }

        this.submitMessage = "(processing)";
        this.checkDoubleSubmit = false;
        this.isSubmitted = false;
        this.checkValid = false;
        this.validationElements = new Map<Element, IValidationElement>();
        this.submitConfirmWarning = null;
        this.useRecaptcha = false;

        if (this.form) {
            this.form.addEventListener("submit", (e) => {
                self.submit(e);
            });
        } else {
            console.warn("FormHelper: form element not found.");
        }
    }

    // Prevent double-submission
    public preventDoubleSubmit() {
        this.checkDoubleSubmit = true;
    }

    // Add a confirmation popup to verify that you want to submit
    // the form?
    public addSubmitConfirmWarning(warning: string) {
        this.submitConfirmWarning = warning;
    }

    // Add the HTML required attribute to fields which have unobtrusive required validation
    // setup
    public addRequiredAttributes() {
        const allfields = this.form.elements;
        for (let idxEl = 0; idxEl < allfields.length; ++idxEl) {
            const field = allfields[idxEl];
            const valrequired = field.getAttribute("data-val-required");
            if (valrequired) {
                field.setAttribute("required", "");
            }
        }
    }

    // Check all elements for unobtrusive elements
    // and try to add our equivalents
    public addUnobtrusiveAttributes() {
        this.form.noValidate = true;
        this.checkValid = true;
        const allfields = this.form.elements;

        for (let idxEl = 0; idxEl < allfields.length; ++idxEl) {
            const field = allfields[idxEl];
            this.addUnobtrusiveField(<FieldElement>field);
        }
    }

    public addRecaptcha(siteKey: string) {
        this.recaptchaField = this.form.querySelector('input[name="g-recaptcha-response"]');
        if (this.recaptchaField) {
            this.useRecaptcha = true;
            this.recaptchaSiteKey = siteKey;
        } else {
            console.error('FormHelper: reCAPTCHA cannot be enabled. The required field <input type="hidden" name="g-recaptcha-response"> was not found in the form.');
        }
    }

    private async submit(e: SubmitEvent) {
        if (this.checkValid) {
            const isValid = this.form.reportValidity();
            if (!isValid) {
                e.preventDefault();
                return;
            }
        }

        if (this.submitConfirmWarning) {
            if (!window.confirm(this.submitConfirmWarning)) {
                e.preventDefault();
                return;
            }
        }

        if (this.checkDoubleSubmit && this.isSubmitted) {
            e.preventDefault();
            return;
        }

        if (!this.useRecaptcha) {
            if (this.checkDoubleSubmit) {
                this.isSubmitted = true;
                this.showSubmitted();
            }
            return; // let normal submit happen
        }

        e.preventDefault();

        if (this.checkDoubleSubmit) {
            this.isSubmitted = true;
            this.showSubmitted();
        }

        if (
            typeof grecaptcha === "undefined" ||
            typeof grecaptcha.enterprise === "undefined" ||
            typeof grecaptcha.enterprise.ready !== "function"
        ) {
            console.error("FormHelper Error: reCAPTCHA Enterprise is not available.");
            this.isSubmitted = false;
            this.setSubmitDisabled(false);
            return;
        }

        grecaptcha.enterprise.ready(async () => {
            try {
                const token = await grecaptcha.enterprise.execute(this.recaptchaSiteKey!, {
                    action: "submit",
                });

                if (!token) {
                    throw new Error("Empty reCAPTCHA token");
                }

                if (this.recaptchaField) {
                    this.recaptchaField.value = token;
                }

                HTMLFormElement.prototype.submit.call(this.form);
            } catch (err) {
                console.error("reCAPTCHA failed", err);
                this.isSubmitted = false;
                this.setSubmitDisabled(false);
            }
        });
    }
    private showSubmitted() {
        this.setSubmitDisabled(true);
        if (this.submitMessage) {
            this.submitButtons.forEach((button) => {
                if (button.nodeName.toLowerCase() === "button") {
                    button.innerHTML = this.submitMessage;
                } else {
                    button.value = this.submitMessage;
                }
            });
        }
    }

    public setSubmitDisabled(isDisabled: boolean) {
        if (isDisabled) {
            this.submitButtons.forEach((button) =>
                button.setAttribute("disabled", "")
            );
        } else {
            this.submitButtons.forEach((button) =>
                button.removeAttribute("disabled")
            );
        }
    }


    private addUnobtrusiveField(field: FieldElement) {
        if (!FormHelper.isFieldElement(field)) {
            return;
        }
        const self = this;

        const validElement: IValidationElement = {
            field: field,
        };

        field.addEventListener("invalid", function handleInvalidField(event) {
            self.handleInvalid(field, event);
        });

        field.addEventListener("blur", function handleFieldBlur() {
            const validEl = self.validationElements.get(field);
            if (validEl) {
                self.clearErrorMessage(validEl);
                field.checkValidity();
            }
        });

        // is there a related field that gets any messages that we have?
        if (field.name) {
            const messageField = this.form.querySelector(
                `[data-valmsg-for="${CSS.escape(field.name)}"]`
            );
            if (messageField) {
                validElement.messageField = messageField;
            }
        }

        var attributes = field.getAttributeNames();
        for (const attrkey of attributes) {
            const fixfunc = fixups[attrkey];
            if (fixfunc) {
                const attr = field.getAttribute(attrkey);
                if (attr) {
                    fixfunc(validElement, attr);
                }
            }
        }

        this.validationElements.set(field, validElement);
    }

    private handleInvalid(field: FieldElement, event: Event) {
        // find the validElement
        event.preventDefault(); // suppress browser error
        const validElement = this.validationElements.get(field);
        if (!validElement) {
            return;
        }

        const validity = field.validity;
        if (!validity) {
            this.setErrorMessage(validElement, undefined);
            return;
        }

        // check for missing
        if (validity.valueMissing && validElement.requiredMessage) {
            this.setErrorMessage(validElement, validElement.requiredMessage);
            return;
        }
        if (validity.typeMismatch) {
            if (validElement.emailMessage) {
                this.setErrorMessage(validElement, validElement.emailMessage);
                return;
            }
            if (validElement.creditcardMessage) {
                this.setErrorMessage(
                    validElement,
                    validElement.creditcardMessage,
                );
                return;
            }
            if (validElement.phoneMessage) {
                this.setErrorMessage(validElement, validElement.phoneMessage);
                return;
            }
        }
        if (validity.tooLong) {
            this.setErrorMessage(validElement, validElement.maxlengthMessage);
            return;
        }
        if (validity.tooShort) {
            this.setErrorMessage(validElement, validElement.minlengthMessage);
            return;
        }
        if (validity.patternMismatch && validElement.regexMessage) {
            this.setErrorMessage(validElement, validElement.regexMessage);
            return;
        }

        // default...fall throught
        this.setErrorMessage(validElement, undefined);
    }

    private setErrorMessage(
        validElement: IValidationElement,
        message?: string,
    ) {
        validElement.field.setAttribute("aria-invalid", "true");
        validElement.field.classList.add("input-validation-error");
        if (validElement.messageField) {
            validElement.messageField.innerHTML = message
                ? message
                : DEFAULTINVALIDMESSAGE;
            validElement.messageField.classList.remove(
                "field-validation-valid",
            );
            validElement.messageField.classList.add("field-validation-error");
        }
    }

    private clearErrorMessage(validElement: IValidationElement) {
        validElement.field.removeAttribute("aria-invalid");
        validElement.field.classList.remove("input-validation-error");

        if (validElement.messageField) {
            validElement.messageField.innerHTML = "";
            validElement.messageField.classList.add("field-validation-valid");
            validElement.messageField.classList.remove(
                "field-validation-error",
            );
        }
    }

    // Get the validity attribute. This probably isn't necessary, but just to
    // be safe
    private static isFieldElement(field: Element): boolean {
        if (
            field instanceof HTMLInputElement ||
            field instanceof HTMLSelectElement ||
            field instanceof HTMLTextAreaElement
        ) {
            return true;
        }
        return false;
    }

    private static getValidButtons(
        buttons: NodeListOf<Element>,
    ): (HTMLInputElement | HTMLButtonElement)[] {
        return Array.from(buttons).filter(
            (btn): btn is HTMLInputElement | HTMLButtonElement =>
                btn instanceof HTMLInputElement ||
                btn instanceof HTMLButtonElement,
        );
    }
}
